import { unwrapEnvelopeData } from '@/shared/api/apiEnvelope';
import { rupeesToPaise, type Paise } from '@/shared/utils/money';

import {
  productImageFromRecord,
  rememberedProductImage,
  rememberProductImage,
} from '../lib/productImage';
import {
  emptyOfflineCart,
  emptyOfflineSession,
  type OfflineCart,
  type OfflineCartItem,
  type OfflineCheckoutResult,
  type OfflineCoupon,
  type OfflineCouponList,
  type OfflineCouponPreview,
  type OfflineCustomerSnapshot,
  type OfflineProduct,
  type OfflineProductSearchResult,
  type OfflineSaleSession,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function pickString(o: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = str(o[key]);
    if (value) return value;
  }
  return '';
}

function pickNumber(o: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value.trim().replace(/,/g, ''));
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

function pickBool(o: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

/** Staff offline money fields are rupees. Convert once, at this boundary. */
function rupeesToPaiseField(value: number | undefined): Paise {
  if (value === undefined) return 0;
  return rupeesToPaise(value);
}

function clampInt(value: number | undefined, fallback: number, min = 0): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(min, Math.floor(value));
}

function pageCountFrom(total: number, limit: number, explicit: number | undefined): number {
  if (explicit !== undefined && explicit > 0) return Math.floor(explicit);
  if (limit <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

/**
 * Staff responses are sometimes the documented JSON body and sometimes the gateway envelope.
 * Clear-cart is `{ success, message }` with no `data`, so a missing `data` field is not an error.
 */
export function unwrapStaffPayload(body: unknown, httpStatus: number): unknown {
  const record = asRecord(body);
  if (!record) return body;
  if (record.success === false) {
    return unwrapEnvelopeData(body, httpStatus);
  }
  if (
    'data' in record &&
    record.data !== undefined &&
    (record.success === true || 'statusCode' in record)
  ) {
    return unwrapEnvelopeData(body, httpStatus);
  }
  return body;
}

export function isClearCartAck(raw: unknown): boolean {
  const record = asRecord(raw);
  if (!record) return false;
  if (record.cart || Array.isArray(record.items)) return false;
  return record.success === true || typeof record.message === 'string';
}

function mapPromotion(raw: unknown): { hasPromotion: boolean; promotionName: string | null } {
  if (!raw || typeof raw !== 'object') return { hasPromotion: false, promotionName: null };
  const record = asRecord(raw);
  if (!record) return { hasPromotion: false, promotionName: null };
  const name = pickString(record, 'name', 'title', 'label', 'code', 'description');
  return { hasPromotion: true, promotionName: name || null };
}

function mapCartItem(
  raw: unknown,
  previousItems: readonly OfflineCartItem[] = [],
): OfflineCartItem | null {
  const record = asRecord(raw);
  if (!record) return null;
  const productId = pickString(record, 'productId', 'product_id', 'id');
  if (!productId) return null;
  const promotion = mapPromotion(record.promotion);
  const fromPayload = productImageFromRecord(record);
  const productImageUrl =
    fromPayload ||
    previousItems.find((item) => item.productId === productId)?.productImageUrl ||
    rememberedProductImage(productId);
  if (productImageUrl) rememberProductImage(productId, productImageUrl);
  return {
    productId,
    productName: pickString(record, 'productName', 'product_name', 'name') || productId,
    productImageUrl,
    unitPricePaise: rupeesToPaiseField(pickNumber(record, 'unitPrice', 'unit_price')),
    baseUnitPricePaise: rupeesToPaiseField(
      pickNumber(record, 'baseUnitPrice', 'base_unit_price') ??
        pickNumber(record, 'unitPrice', 'unit_price'),
    ),
    mrpPaise: rupeesToPaiseField(pickNumber(record, 'mrp')),
    quantity: clampInt(pickNumber(record, 'quantity'), 0),
    unit: pickString(record, 'unit'),
    lineDiscountPaise: rupeesToPaiseField(
      pickNumber(record, 'lineDiscountAmount', 'line_discount_amount'),
    ),
    promotionName: promotion.promotionName,
    hasPromotion: promotion.hasPromotion,
  };
}

function mapCart(raw: unknown, previous?: OfflineCart): OfflineCart {
  const record = asRecord(raw);
  if (!record || !Array.isArray(record.items)) return previous ?? emptyOfflineCart();
  const items = record.items
    .map((item) => mapCartItem(item, previous?.items ?? []))
    .filter((item): item is OfflineCartItem => item !== null);
  const totalRupees = pickNumber(
    record,
    'payableTotalRupeesRounded',
    'payable_total_rupees_rounded',
  );
  return {
    userId: pickString(record, 'userId', 'user_id'),
    storeId: pickString(record, 'storeId', 'store_id'),
    items,
    subtotalPaise: rupeesToPaiseField(pickNumber(record, 'subtotal')),
    handlingChargePaise: rupeesToPaiseField(
      pickNumber(record, 'handlingChargeRupees', 'handling_charge_rupees'),
    ),
    deliveryChargePaise: rupeesToPaiseField(
      pickNumber(record, 'deliveryChargeRupees', 'delivery_charge_rupees'),
    ),
    payableTotalPaise: rupeesToPaiseField(totalRupees),
    discountPaise: rupeesToPaiseField(pickNumber(record, 'discountAmount', 'discount_amount')),
    cartValueRupees: 0,
  };
}

/**
 * Identify wraps the sale as `{ found, customer, cart: { cart, customerId, ... } }`.
 * Cart mutations return the sale itself: `{ cart: { items }, customerId, ... }`.
 */
function sessionPayload(raw: unknown): Record<string, unknown> {
  const root = asRecord(raw) ?? {};
  const nested = asRecord(root.cart);
  if (!nested || Array.isArray(nested.items)) return root;
  const innerCart = asRecord(nested.cart);
  const hasCustomer =
    'customerId' in nested ||
    'customerRegistered' in nested ||
    'customerPhoneNumber' in nested ||
    'customerName' in nested;
  if (innerCart || hasCustomer) return nested;
  return root;
}

export function mapOfflineSession(raw: unknown, previous?: OfflineSaleSession): OfflineSaleSession {
  const root = sessionPayload(raw);
  const cartSource = root.cart ?? (Array.isArray(root.items) ? root : undefined);
  const cart = mapCart(cartSource, previous?.cart);
  const totalAmountRupees =
    pickNumber(root, 'totalAmount', 'total_amount') ?? previous?.totalAmountRupees ?? 0;
  const couponDiscountRupees =
    pickNumber(root, 'couponDiscount', 'coupon_discount') ?? previous?.couponDiscountRupees ?? 0;
  const cartValueRupees =
    totalAmountRupees + couponDiscountRupees > 0
      ? totalAmountRupees + couponDiscountRupees
      : cart.cartValueRupees;
  return {
    cart: { ...cart, cartValueRupees },
    customerId: pickString(root, 'customerId', 'customer_id') || previous?.customerId || '',
    customerPhoneNumber:
      'customerPhoneNumber' in root || 'customer_phone_number' in root
        ? pickString(root, 'customerPhoneNumber', 'customer_phone_number')
        : (previous?.customerPhoneNumber ?? ''),
    customerName:
      'customerName' in root || 'customer_name' in root
        ? pickString(root, 'customerName', 'customer_name')
        : (previous?.customerName ?? ''),
    customerRegistered:
      pickBool(root, 'customerRegistered', 'customer_registered') ??
      previous?.customerRegistered ??
      false,
    couponDiscountPaise: rupeesToPaiseField(couponDiscountRupees),
    totalAmountPaise: rupeesToPaiseField(totalAmountRupees),
    totalAmountRupees,
    couponDiscountRupees,
  };
}

export function mapCustomerSnapshot(raw: unknown): OfflineCustomerSnapshot | null {
  const root = asRecord(raw);
  if (!root) return null;
  if (pickBool(root, 'found') === undefined && pickBool(root, 'registered') === undefined) {
    return null;
  }
  const customer = asRecord(root.customer) ?? {};
  return {
    found: pickBool(root, 'found') ?? false,
    registered: pickBool(root, 'registered', 'customerRegistered', 'customer_registered') ?? false,
    fullName:
      pickString(customer, 'fullName', 'full_name') ||
      pickString(root, 'customerName', 'customer_name'),
    phoneNumber:
      pickString(customer, 'phoneNumber', 'phone_number') ||
      pickString(root, 'customerPhoneNumber', 'customer_phone_number'),
    email: pickString(customer, 'email'),
    status: pickString(customer, 'status'),
  };
}

function mapProduct(raw: unknown): OfflineProduct | null {
  const record = asRecord(raw);
  if (!record) return null;
  const productId = pickString(record, 'productId', 'product_id', 'id');
  if (!productId) return null;
  const quantity = clampInt(pickNumber(record, 'quantity', 'stock'), 0);
  const inStock = pickBool(record, 'inStock', 'in_stock') ?? quantity > 0;
  const primaryImageUrl = productImageFromRecord(record);
  if (primaryImageUrl) rememberProductImage(productId, primaryImageUrl);
  return {
    productId,
    name: pickString(record, 'name', 'productName', 'product_name') || productId,
    unit: pickString(record, 'unit'),
    primaryImageUrl,
    mrpPaise: rupeesToPaiseField(pickNumber(record, 'mrp')),
    normalSellingPricePaise: rupeesToPaiseField(
      pickNumber(record, 'normalSellingPrice', 'normal_selling_price', 'sellingPrice'),
    ),
    customerSellingPricePaise: rupeesToPaiseField(
      pickNumber(record, 'customerSellingPrice', 'customer_selling_price') ??
        pickNumber(record, 'normalSellingPrice', 'normal_selling_price'),
    ),
    quantity,
    inStock,
  };
}

export function mapProductSearch(
  raw: unknown,
  fallbackPage: number,
  fallbackLimit: number,
): OfflineProductSearchResult {
  const list = Array.isArray(raw) ? raw : (asRecord(raw)?.items ?? asRecord(raw)?.products);
  const items = (Array.isArray(list) ? list : [])
    .map(mapProduct)
    .filter((item): item is OfflineProduct => item !== null);
  const record = asRecord(raw) ?? {};
  const limit = clampInt(pickNumber(record, 'limit'), fallbackLimit, 1);
  const total = clampInt(pickNumber(record, 'total'), items.length);
  const page = clampInt(pickNumber(record, 'page'), fallbackPage, 1);
  const pageCount = pageCountFrom(
    total,
    limit,
    pickNumber(record, 'totalPages', 'total_pages', 'pageCount', 'page_count'),
  );
  return { items, total, page, limit, pageCount };
}

function mapCoupon(raw: unknown): OfflineCoupon | null {
  const record = asRecord(raw);
  if (!record) return null;
  const id = pickString(record, 'id');
  const code = pickString(record, 'code');
  if (!id && !code) return null;
  const discountKind = pickString(record, 'discountKind', 'discount_kind') || 'FLAT';
  const rawDiscount = pickNumber(record, 'discountValue', 'discount_value') ?? 0;
  const maxRaw = pickNumber(record, 'maxDiscountAmount', 'max_discount_amount');
  return {
    id: id || code,
    code,
    description: pickString(record, 'description'),
    type: pickString(record, 'type'),
    discountKind,
    discountValue: discountKind === 'PERCENTAGE' ? rawDiscount : rupeesToPaiseField(rawDiscount),
    maxDiscountAmountPaise: maxRaw === undefined ? null : rupeesToPaiseField(maxRaw),
    minOrderValuePaise: rupeesToPaiseField(pickNumber(record, 'minOrderValue', 'min_order_value')),
    startsAt: pickString(record, 'startsAt', 'starts_at') || null,
    endsAt: pickString(record, 'endsAt', 'ends_at') || null,
    isActive: pickBool(record, 'isActive', 'is_active') ?? true,
  };
}

export function mapCouponList(raw: unknown): OfflineCouponList {
  const record = asRecord(raw) ?? {};
  const list = Array.isArray(raw) ? raw : (record.items ?? []);
  const items = (Array.isArray(list) ? list : [])
    .map(mapCoupon)
    .filter((item): item is OfflineCoupon => item !== null);
  const limit = clampInt(pickNumber(record, 'limit'), items.length, 0);
  const total = clampInt(pickNumber(record, 'total'), items.length);
  const page = clampInt(pickNumber(record, 'page'), 1, 1);
  return {
    items,
    total,
    page,
    limit,
    pageCount: pageCountFrom(
      total,
      limit || 1,
      pickNumber(record, 'totalPages', 'total_pages', 'pageCount'),
    ),
  };
}

export function mapCouponPreview(raw: unknown): OfflineCouponPreview {
  const record = asRecord(raw) ?? {};
  return {
    valid: pickBool(record, 'valid') ?? false,
    message: pickString(record, 'message'),
    discountPaise: rupeesToPaiseField(pickNumber(record, 'discountAmount', 'discount_amount')),
    totalAfterDiscountPaise: rupeesToPaiseField(
      pickNumber(record, 'totalAfterDiscount', 'total_after_discount'),
    ),
  };
}

export function mapCheckoutResult(raw: unknown): OfflineCheckoutResult {
  const record = asRecord(raw) ?? {};
  const amountPaisa = pickNumber(record, 'amountPaisa', 'amount_paisa');
  const totalRupees = pickNumber(record, 'totalAmount', 'total_amount');
  const totalAmountPaise =
    amountPaisa !== undefined && Number.isInteger(amountPaisa)
      ? amountPaisa
      : rupeesToPaiseField(totalRupees);
  return {
    orderId: pickString(record, 'orderId', 'order_id'),
    orderNumber: pickString(record, 'orderNumber', 'order_number'),
    status: pickString(record, 'status'),
    totalAmountPaise,
    message: pickString(record, 'message'),
    currency: pickString(record, 'currency') || 'INR',
    paymentStatus: pickString(record, 'paymentStatus', 'payment_status'),
  };
}

export function clearedSession(): OfflineSaleSession {
  return emptyOfflineSession();
}
